import { db, auth } from './firebaseClient';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';

/**
 * Creates a CRUD service for a Firestore collection
 * Maintains API compatibility with Base44 SDK
 */
function createEntityService(collectionName) {
  return {
    /**
     * List all documents in the collection
     * @param {Object} filters - Optional filters { field: value }
     * @param {Object} options - Optional { orderBy: 'field', limit: number }
     */
    async list(filters = {}, options = {}) {
      let ref = collection(db, collectionName);
      let q = query(ref);

      // Apply filters
      Object.entries(filters).forEach(([field, value]) => {
        if (value !== undefined && value !== null) {
          q = query(q, where(field, '==', value));
        }
      });

      // Apply ordering
      if (options.orderBy) {
        const direction = options.orderDirection || 'asc';
        q = query(q, orderBy(options.orderBy, direction));
      }

      // Apply limit
      if (options.limit) {
        q = query(q, limit(options.limit));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        // Convert Firestore timestamps to ISO strings for compatibility
        created_date: doc.data().created_date?.toDate?.()?.toISOString() || doc.data().created_date,
        updated_date: doc.data().updated_date?.toDate?.()?.toISOString() || doc.data().updated_date,
      }));
    },

    /**
     * Get a single document by ID
     */
    async get(id) {
      const docRef = doc(db, collectionName, id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) return null;
      
      const data = docSnap.data();
      return { 
        id: docSnap.id, 
        ...data,
        created_date: data.created_date?.toDate?.()?.toISOString() || data.created_date,
        updated_date: data.updated_date?.toDate?.()?.toISOString() || data.updated_date,
      };
    },

    /**
     * Create a new document
     */
    async create(data) {
      const ref = collection(db, collectionName);
      const currentUser = auth.currentUser;
      
      const docData = {
        ...data,
        created_date: new Date().toISOString(),
        updated_date: new Date().toISOString(),
        created_by: currentUser?.email || 'system',
        created_by_id: currentUser?.uid || null,
      };

      // Asignar propietario del lead/entidad si se crea manualmente y no tiene uno
      if (currentUser?.uid && !docData.owner_id) {
        docData.owner_id = currentUser.uid;
        docData.owner_email = currentUser.email;
      }

      const docRef = await addDoc(ref, docData);
      return { id: docRef.id, ...docData };
    },

    /**
     * Update an existing document
     */
    async update(id, data) {
      const docRef = doc(db, collectionName, id);
      const currentUser = auth.currentUser;
      
      const updateData = { 
        ...data, 
        updated_date: new Date().toISOString(),
        ultimo_usuario_edicion: currentUser?.email || null,
      };
      
      await updateDoc(docRef, updateData);
      return { id, ...updateData };
    },

    /**
     * Delete a document
     */
    async delete(id) {
      const docRef = doc(db, collectionName, id);
      await deleteDoc(docRef);
    },

    /**
     * Subscribe to real-time updates
     */
    subscribe(callback, filters = {}, onError = null) {
      let ref = collection(db, collectionName);
      let q = query(ref);

      Object.entries(filters).forEach(([field, value]) => {
        if (value !== undefined && value !== null) {
          q = query(q, where(field, '==', value));
        }
      });

      return onSnapshot(q, (snapshot) => {
        const docs = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(),
          created_date: doc.data().created_date?.toDate?.()?.toISOString() || doc.data().created_date,
          updated_date: doc.data().updated_date?.toDate?.()?.toISOString() || doc.data().updated_date,
        }));
        callback(docs);
      }, (error) => {
        console.error(`Firestore subscribe error for ${collectionName}:`, error);
        if (onError) onError(error);
      });
    },

    /**
     * Filter helper for complex queries (Base44 compatibility)
     */
    filter(field) {
      return {
        eq: (value) => ({ [field]: value }),
        in: (values) => ({ [`${field}_in`]: values }),
      };
    }
  };
}

// Entity services matching Base44 schema
export const Lead = createEntityService('leads');
export const EmailMessage = createEntityService('emailMessages');
export const Presupuesto = createEntityService('presupuestos');
export const SeguimientoEmail = createEntityService('seguimientosEmail');
export const Categoria = createEntityService('categorias');
export const Producto = createEntityService('productos');
export const Actividad = createEntityService('actividades');
export const WebhookConfig = createEntityService('webhookConfigs');
export const DiscordAlert = createEntityService('discordAlerts');
export const ActivityLog = createEntityService('activityLogs');
export const Settings = createEntityService('settings');
export const Setting = Settings; // Alias
export const RoadmapItem = createEntityService('roadmapItems');
export const SystemUser = createEntityService('users'); // Service for actual users collection
export const SystemNotification = createEntityService('notifications'); // Service for notifications
export const CatalogoTruyol = createEntityService('catalogo_truyol');
export const CatalogoTruyolMaestro = createEntityService('catalogo_truyol_maestro');
export const CasoRespuesta = createEntityService('casosRespuesta');

/**
 * Query helper for Base44 compatibility
 * Usage: Query.list('leads', { embudo: 'recibidos' })
 */
export const Query = {
  async list(collectionName, filters = {}, options = {}) {
    return createEntityService(collectionName).list(filters, options);
  },
  
  // Get entity service by name
  getEntity(name) {
    const entities = {
      Lead, EmailMessage, Presupuesto, SeguimientoEmail,
      Categoria, Producto, Actividad, WebhookConfig,
      DiscordAlert, ActivityLog, Settings, RoadmapItem, SystemUser, SystemNotification
    };
    return entities[name];
  }
};

// Export auth for User compatibility
export const User = auth;